import { Box, Flex, Link, SimpleGrid, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { OWNERSHIP_NOTE, TIERS } from "./homeContent";
import { Body, Card, CheckList, Cta, H3, Inner, Intro, Section, useHomeTokens } from "./primitives";

/**
 * The ownership note under the plan grid — the ONE-SENTENCE version. The plans
 * above are managed services; none converts into source code; owning it is a
 * published rung; leaving is free. The ladder itself lives on /ownership and in
 * /terms §2 and is deliberately not repeated here (2026-09-06: it was, twice,
 * in two overlapping grids that ran longer than the pricing grid above them).
 * The hero's asterisk lands here (`id="ownership-note"`).
 */
const OwnershipNote = () => {
  const t = useHomeTokens();
  return (
    <Box
      id="ownership-note"
      mt="32px"
      p={{ base: 5, md: "30px 32px" }}
      borderRadius="20px"
      borderWidth="1px"
      borderColor={t.cardBorder}
      bg={t.cardTint}
      display="flex"
      flexDirection="column"
      gap="16px"
      scrollMarginTop="96px"
    >
      <Flex align="baseline" gap="12px">
        <Text as="span" fontSize="1.5rem" lineHeight="1" color={t.primary} aria-hidden="true">
          *
        </Text>
        <H3 fontSize="1.15rem">{OWNERSHIP_NOTE.heading}</H3>
      </Flex>
      <Body fontSize=".97rem" lineHeight="1.65">{OWNERSHIP_NOTE.body}</Body>
      <Body fontSize=".97rem" lineHeight="1.65" color={t.text} fontWeight="500">
        {OWNERSHIP_NOTE.closer}
      </Body>

      <Flex wrap="wrap" gap="20px" align="center" mt="4px">
        <Cta
          to="/ownership"
          size="sm"
          title="Opens the ownership page — every rung from the free data export to a data centre of your own, with its published price and what you still pay. Reading only."
        >
          See every rung and its price →
        </Cta>
        <Link
          as={RouterLink}
          to="/terms"
          fontSize=".92rem"
          fontWeight="500"
          color={t.kicker}
          title="Opens the full terms of working with TMS — what you are buying, your data rights, and the published price of every exit. Reading only."
          _hover={{ textDecoration: "underline", color: t.secondary }}
        >
          Read the full terms of working with TMS
        </Link>
      </Flex>
    </Box>
  );
};

/** #pricing — "Pick the tier that matches your stage" + the ownership note. */
export const Pricing = () => {
  const t = useHomeTokens();
  return (
    <Section id="pricing">
      <Inner>
        <Intro
          kicker="Subscription plans"
          title="Pick the tier that matches your stage"
          lead="Every plan includes a professional website, secure authentication and profile management."
          maxW="700px"
        />
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} gap="14px" alignItems="stretch">
          {TIERS.map((tier) => (
            <Card key={tier.name} p="30px 26px" borderRadius="20px" gap="16px">
              <Flex align="center" gap="10px">
                <Box as="span" fontSize="17px" lineHeight="1" aria-hidden="true">
                  {tier.icon}
                </Box>
                <H3 fontSize="1.15rem">{tier.name}</H3>
              </Flex>
              <Text fontSize="2.1rem" fontWeight="600" letterSpacing="-0.035em" lineHeight="1" color={t.text}>
                {tier.price}
              </Text>
              <Body fontSize=".9rem" lineHeight="1.55" color={t.textMuted}>
                {tier.blurb}
              </Body>
              <CheckList items={tier.points} gap="9px" mt="6px" pt="18px" borderTop="1px solid" borderColor={t.hairline} />
              <Link
                href="#contact"
                mt="auto"
                pt="22px"
                fontSize=".9rem"
                fontWeight="500"
                color={t.kicker}
                title={`Jumps to the contact section to start on ${tier.name}. Nothing is purchased on this page.`}
                _hover={{ textDecoration: "none", color: t.secondary }}
              >
                Start on {tier.name} →
              </Link>
            </Card>
          ))}
        </SimpleGrid>
        <OwnershipNote />
      </Inner>
    </Section>
  );
};
